import React, { useEffect } from "react";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { fetchGet } from "../../../fetchers";
import LOGO_RKS from "../../../assets/logo/LOGORKS.png";
import { Colors } from "../../../assets/Colors";

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Helvetica",
    fontSize: 10,
  },
  container: {
    flex: 1,
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 25,
    borderBottom: "2pt solid #000",
    paddingBottom: 15,
  },
  logoContainer: {
    width: 70,
    height: 70,
    flexShrink: 0,
  },
  logo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: "center",
    marginHorizontal: 10,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    color: "#2c5282",
  },
  companyInfo: {
    fontSize: 8,
    textAlign: "center",
    color: "#666",
    marginTop: 5,
  },
  mainSection: {
    marginBottom: 20,
  },
  infoSection: {
    backgroundColor: "#f8f9fa",
    padding: 15,
    border: "1pt solid #ddd",
    borderRadius: 5,
    marginBottom: 20,
  },
  row: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  label: {
    width: 100,
    fontWeight: "bold",
    color: "#2d3748",
    marginRight: 5,
  },
  colon: {
    width: 10,
    marginRight: 8,
    fontWeight: "bold",
  },
  value: {
    flex: 1,
    textAlign: "left",
    color: "#4a5568",
  },
  subjectSection: {
    backgroundColor: "#e6f7ff",
    padding: 12,
    border: "1pt solid #91d5ff",
    borderRadius: 5,
    marginBottom: 20,
  },
  subjectLabel: {
    width: 100,
    fontWeight: "bold",
    color: "#1890ff",
    marginRight: 5,
  },
  subjectValue: {
    flex: 1,
    fontWeight: "bold",
    fontSize: 12,
    color: "#0050b3",
  },
  descSection: {
    border: "1pt solid #d9d9d9",
    padding: 15,
    borderRadius: 5,
    marginBottom: 25,
    backgroundColor: "#fff",
    minHeight: 150,
  },
  descHeader: {
    flexDirection: "row",
    marginBottom: 10,
    borderBottom: "1pt solid #eee",
    paddingBottom: 8,
  },
  descLabel: {
    fontWeight: "bold",
    color: "#2d3748",
    marginRight: 8,
  },
  descContent: {
    padding: 10,
    backgroundColor: "#fafafa",
    border: "1pt solid #f0f0f0",
    borderRadius: 3,
    minHeight: 100,
  },
  descText: {
    fontSize: 10,
    lineHeight: 1.5,
    textAlign: "justify",
    color: "#4a5568",
  },
  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 40,
    paddingTop: 20,
    borderTop: "1pt dashed #ccc",
  },
  signatureBox: {
    alignItems: "center",
    width: "30%",
  },
  signatureLabel: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 25,
    color: "#2d3748",
  },
  signatureLine: {
    width: 120,
    height: 1,
    backgroundColor: "#000",
    marginBottom: 8,
  },
  signatureName: {
    fontSize: 10,
    color: "#4a5568",
    fontStyle: "italic",
  },
  dateLabel: {
    fontSize: 8,
    color: "#718096",
    marginTop: 5,
  },
  statusBadge: {
    padding: "2pt 8pt",
    borderRadius: 10,
    fontSize: 8,
    fontWeight: "bold",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 8,
    color: "#718096",
    borderTop: "1pt solid #eee",
    paddingTop: 10,
  },
  fileLink: {
    color: "#1890ff",
    textDecoration: "underline",
  },
  riwayatSection: {
    marginTop: 20,
    borderTop: "1pt solid #ddd",
    paddingTop: 20,
  },
  riwayatTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 15,
    color: "#2d3748",
    backgroundColor: "#f8f9fa",
    padding: 8,
    borderLeft: "3pt solid #1890ff",
  },
  riwayatItem: {
    marginBottom: 20,
    padding: 15,
    border: "1pt solid #e9ecef",
    borderRadius: 5,
    backgroundColor: "#fff",
  },
  riwayatHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: "1pt solid #f0f0f0",
  },
  riwayatFrom: {
    fontWeight: "bold",
    fontSize: 10,
    color: "#2d3748",
  },
  riwayatDate: {
    fontSize: 8,
    color: "#718096",
  },
  riwayatContent: {
    padding: 10,
    backgroundColor: "#fafafa",
    borderRadius: 3,
    marginTop: 8,
  },
  noRiwayat: {
    textAlign: "center",
    color: "#718096",
    fontStyle: "italic",
    padding: 20,
  },
  memoNumber: {
    fontSize: 9,
    fontFamily: "Courier",
    backgroundColor: "#f8f9fa",
    padding: "3pt 6pt",
    borderRadius: 3,
    marginTop: 3,
  },
});

// Helper functions similar to first component
const safeString = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value === "object") {
    if (value.String !== undefined) return String(value.String);
    if (value.Valid !== undefined) return String(value.Valid);
    if (value.value !== undefined) return String(value.value);
    if (value.name !== undefined) return String(value.name);
    if (value.Name !== undefined) return String(value.Name);
    if (value.NIK !== undefined) return String(value.NIK);
    if (value.nik !== undefined) return String(value.nik);
    if (value.Nik !== undefined) return String(value.Nik);
    return JSON.stringify(value);
  }
  return String(value);
};

const getFieldValue = (obj, fieldName) => {
  if (!obj || !fieldName) return null;

  const possibleCases = [
    fieldName,
    fieldName.toLowerCase(),
    fieldName.toUpperCase(),
    fieldName.charAt(0).toLowerCase() + fieldName.slice(1),
    fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
  ];

  for (const caseVariant of possibleCases) {
    if (obj[caseVariant] !== undefined && obj[caseVariant] !== null) {
      return obj[caseVariant];
    }
  }

  for (const key in obj) {
    if (obj[key] && typeof obj[key] === "object") {
      for (const caseVariant of possibleCases) {
        if (
          obj[key][caseVariant] !== undefined &&
          obj[key][caseVariant] !== null
        ) {
          return obj[key][caseVariant];
        }
      }
    }
  }

  return null;
};

const getNameFromObject = (obj) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  if (typeof obj === "object") {
    return safeString(
      obj.Name ||
        obj.name ||
        obj.NAMA ||
        obj.From ||
        obj.from ||
        obj.Nama ||
        obj.nama,
    );
  }
  return String(obj);
};

const getNikFromObject = (obj) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  if (typeof obj === "object") {
    return safeString(
      obj.Nik || obj.nik || obj.NIK || obj.id || obj.Id || obj.ID,
    );
  }
  return String(obj);
};

const getArrayValue = (obj, fieldName) => {
  const value = getFieldValue(obj, fieldName);
  return Array.isArray(value) ? value : [];
};

const getMemoNumber = (data) => {
  const noMemo = getFieldValue(data, "noMemo");
  const nomorMemo = getFieldValue(data, "nomorMemo");

  if (noMemo && noMemo.trim() !== "") {
    return safeString(noMemo);
  }
  if (nomorMemo && nomorMemo.trim() !== "") {
    return safeString(nomorMemo);
  }
  return "-";
};

const formatDateDisplay = (dateString) => {
  try {
    if (!dateString) return "";

    // Normalisasi: ganti T jadi spasi, buang ms & timezone
    const clean = dateString
      .toString()
      .replace("T", " ")
      .split(".")[0]
      .split("Z")[0]
      .split("+")[0];

    // expected: YYYY-MM-DD HH:mm:ss
    const [datePart, timePart = "00:00:00"] = clean.split(" ");
    if (!datePart) return safeString(dateString);

    const [year, month, day] = datePart.split("-");
    const [hours, minutes] = timePart.split(":");

    if (!year || !month || !day) return safeString(dateString);

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return safeString(dateString);
  }
};


const getStatusText = (status) => {
  const approved = parseInt(safeString(status));
  if (approved === 99) return "Close";
  if (approved === 0) return "Progress";
  return "Open";
};

const getStatusColor = (status) => {
  const approved = parseInt(safeString(status));
  if (approved === 99) return { bg: "#e6fffa", text: "#234e52" };
  if (approved === 0) return { bg: "#fefcbf", text: "#744210" };
  return { bg: "#bee3f8", text: "#2c5282" };
};

// Clean HTML content for PDF
const cleanHtmlContent = (html) => {
  if (!html) return "";

  // Remove HTML tags but preserve line breaks
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<p>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\n\s*\n/g, "\n")
    .replace(/\n+/g, "\n")
    .trim();
};

// Main PDF Component
const ReportMemoPdfReturn = (props) => {
  const { data } = props.data;

  if (!data) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Data tidak tersedia untuk dicetak</Text>
        </Page>
      </Document>
    );
  }

  // Get main memo data
  const memoNumber = getMemoNumber(data);
  const memoId = safeString(getFieldValue(data, "id") || "-");
  const memoFrom = getNameFromObject(getFieldValue(data, "from"));
  const memoFromNik = getNikFromObject(getFieldValue(data, "nikFrom"));
  const memoDate = formatDateDisplay(getFieldValue(data, "date"));
  const memoSubject = safeString(getFieldValue(data, "subject"));
  const memoDesc = safeString(getFieldValue(data, "desc"));
  const memoFile = safeString(getFieldValue(data, "file"));
  const memoStatus = getFieldValue(data, "approved");
  const statusText = getStatusText(memoStatus);
  const statusColors = getStatusColor(memoStatus);

  // Get to list
  const toList = getArrayValue(data, "toList");
  const ccList = getArrayValue(data, "ccList");

  // Get riwayat balasan
  const riwayatList = getArrayValue(data, "list");

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header with Logo and Title */}
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Image style={styles.logo} src={LOGO_RKS} cache={false} />
            </View>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle}>INTERNAL MEMO</Text>
              <Text style={styles.companyInfo}>
                PT. RKS - Official Document
              </Text>
            </View>
            <View style={styles.logoContainer} />
          </View>

          {/* Main Information Section */}
          <View style={styles.infoSection}>
            <View style={styles.row}>
              <Text style={styles.label}>Nomor Memo</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                <Text style={styles.memoNumber}>{memoNumber}</Text>
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>ID Memo</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>{memoId}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Status</Text>
              <Text style={styles.colon}>:</Text>
              <View style={[styles.value, { flexDirection: "row" }]}>
                <Text
                  style={[
                    styles.statusBadge,
                    {
                      backgroundColor: statusColors.bg,
                      color: statusColors.text,
                    },
                  ]}
                >
                  {statusText}
                </Text>
              </View>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Dibuat Oleh</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {memoFrom}
                {memoFromNik && memoFromNik !== "" && (
                  <Text style={{ fontSize: 8, color: "#666", marginLeft: 5 }}>
                    (NIK: {memoFromNik})
                  </Text>
                )}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Tanggal Dibuat</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>{memoDate}</Text>
            </View>

            {/* To List */}
            <View style={styles.row}>
              <Text style={styles.label}>Kepada</Text>
              <Text style={styles.colon}>:</Text>
              <View style={styles.value}>
                {toList.length > 0 ? (
                  toList.map((to, i) => (
                    <Text key={i}>
                      {getNameFromObject(to)}
                      {getNikFromObject(to) && getNikFromObject(to) !== "" && (
                        <Text
                          style={{ fontSize: 8, color: "#666", marginLeft: 5 }}
                        >
                          ({getNikFromObject(to)})
                        </Text>
                      )}
                      {i < toList.length - 1 ? ", " : ""}
                    </Text>
                  ))
                ) : (
                  <Text style={{ fontStyle: "italic", color: "#999" }}>-</Text>
                )}
              </View>
            </View>

            {/* CC List */}
            <View style={styles.row}>
              <Text style={styles.label}>CC</Text>
              <Text style={styles.colon}>:</Text>
              <View style={styles.value}>
                {ccList.length > 0 ? (
                  ccList.map((cc, i) => (
                    <Text key={i}>
                      {getNameFromObject(cc)}
                      {getNikFromObject(cc) && getNikFromObject(cc) !== "" && (
                        <Text
                          style={{ fontSize: 8, color: "#666", marginLeft: 5 }}
                        >
                          ({getNikFromObject(cc)})
                        </Text>
                      )}
                      {i < ccList.length - 1 ? ", " : ""}
                    </Text>
                  ))
                ) : (
                  <Text style={{ fontStyle: "italic", color: "#999" }}>-</Text>
                )}
              </View>
            </View>

            {/* Attachment */}
            <View style={styles.row}>
              <Text style={styles.label}>Attachment</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {memoFile && memoFile.trim() !== "" ? (
                  <Text style={styles.fileLink}>
                    📎 File tersedia ({safeString(memoFile)})
                  </Text>
                ) : (
                  <Text style={{ fontStyle: "italic", color: "#999" }}>
                    Tidak ada attachment
                  </Text>
                )}
              </Text>
            </View>
          </View>

          {/* Subject Section */}
          <View style={styles.subjectSection}>
            <View style={styles.row}>
              <Text style={styles.subjectLabel}>SUBJEK</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.subjectValue}>
                {memoSubject || "Tidak ada subjek"}
              </Text>
            </View>
          </View>

          {/* Description Section */}
          <View style={styles.descSection}>
            <View style={styles.descHeader}>
              <Text style={styles.descLabel}>DESKRIPSI</Text>
              <Text style={styles.colon}>:</Text>
            </View>
            <View style={styles.descContent}>
              <Text style={styles.descText}>
                {cleanHtmlContent(memoDesc) || "Tidak ada deskripsi"}
              </Text>
            </View>
          </View>

          {/* Riwayat Balasan Section */}
          {/* {riwayatList.length > 0 && (
            <View style={styles.riwayatSection}>
              <Text style={styles.riwayatTitle}>
                RIWAYAT BALASAN ({riwayatList.length})
              </Text>
              
              {riwayatList.map((item, i) => {
                const itemFrom = getNameFromObject(getFieldValue(item, 'from'));
                const itemDate = formatDateDisplay(getFieldValue(item, 'date'));
                const itemDesc = safeString(getFieldValue(item, 'desc'));
                const itemFile = safeString(getFieldValue(item, 'file'));
                const itemStatus = getFieldValue(item, 'approved');
                const itemStatusText = getStatusText(itemStatus);
                const itemStatusColors = getStatusColor(itemStatus);
                const itemNoMemo = getFieldValue(item, 'noMemo') || getFieldValue(item, 'nomorMemo');

                return (
                  <View key={i} style={styles.riwayatItem}>
                    <View style={styles.riwayatHeader}>
                      <View style={{ flexDirection: 'column' }}>
                        <Text style={styles.riwayatFrom}>{itemFrom}</Text>
                        <Text style={styles.riwayatDate}>{itemDate}</Text>
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {itemNoMemo && (
                          <Text style={[styles.memoNumber, { marginRight: 10 }]}>
                            {safeString(itemNoMemo)}
                          </Text>
                        )}
                        <Text style={[
                          styles.statusBadge, 
                          { 
                            backgroundColor: itemStatusColors.bg,
                            color: itemStatusColors.text,
                            fontSize: 7
                          }
                        ]}>
                          {itemStatusText}
                        </Text>
                      </View>
                    </View>
                    
                    {itemDesc && itemDesc.trim() !== "" && (
                      <View style={styles.riwayatContent}>
                        <Text style={styles.descText}>
                          {cleanHtmlContent(itemDesc)}
                        </Text>
                      </View>
                    )}
                    
                    {itemFile && itemFile.trim() !== "" && (
                      <View style={{ marginTop: 8 }}>
                        <Text style={{ fontSize: 8, color: '#666' }}>
                          📎 Attachment: {safeString(itemFile)}
                        </Text>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          )}

          {riwayatList.length === 0 && (
            <View style={styles.riwayatSection}>
              <Text style={styles.riwayatTitle}>RIWAYAT BALASAN</Text>
              <Text style={styles.noRiwayat}>
                Belum ada riwayat balasan
              </Text>
            </View>
          )} */}

          {/* Signatures Section */}
          <View style={styles.signatureSection}>
            <View style={styles.signatureBox}>
              <Text style={styles.signatureLabel}>Yang Mengajukan</Text>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}>{memoFrom || ""}</Text>
              <Text style={styles.dateLabel}>{memoDate}</Text>
            </View>

            <View style={styles.signatureBox}>
              <Text style={styles.signatureLabel}>Menyetujui</Text>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}> </Text>
              <Text style={styles.dateLabel}>Tanggal: ___________</Text>
            </View>

            <View style={styles.signatureBox}>
              <Text style={styles.signatureLabel}>Mengetahui</Text>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}> </Text>
              <Text style={styles.dateLabel}>Tanggal: ___________</Text>
            </View>
          </View>

          {/* Footer */}
          <Text style={styles.footer}>
            Dokumen ini dicetak secara elektronik - PT. RKS ©{" "}
            {new Date().getFullYear()}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Main Component
const ReportMemoPdf = () => {
  const params = useParams();
  const mutation = useMutation((id) => fetchGet("getInternalMemoDetail/" + id));

  useEffect(() => {
    if (params.id) {
      mutation.mutate(params.id);
    }
  }, [params.id]);

  return (
    <div className="md:ml-64">
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
        </div>
      </div>
      <div className="px-3 md:px-8 h-screen -mt-32">
        {mutation.data && (
          <PDFViewer className="h-screen w-full">
            <ReportMemoPdfReturn data={mutation.data} />
          </PDFViewer>
        )}
        {mutation.isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-lg">Loading document...</div>
          </div>
        )}
        {mutation.isError && (
          <div className="flex items-center justify-center h-full">
            <div className="text-lg text-red-600">Error loading document</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportMemoPdf;
